// Single owner of the pdf.js worker configuration.
//
// pdf.js parses documents in a Web Worker; GlobalWorkerOptions.workerSrc is a
// module-level singleton (last write wins), so exactly one module — this one —
// may assign it. All call sites obtain pdf.js via loadPdfjs() and must never
// touch GlobalWorkerOptions themselves. (Three files once assigned it
// independently; the one that pointed at a CDN made rendering order-dependent
// and silently blank under corporate egress filtering.)
//
// The worker is a webpack-emitted asset served from our own origin:
// `new URL(..., import.meta.url)` makes webpack resolve pdf.worker.min.js out
// of node_modules and emit it as build/static/media/pdf.worker.min.[hash].js.
// This replaces two prior failure modes:
//   - CDN worker (cdn.jsdelivr.net): blocked by corporate egress / Zero Trust
//     proxies → the worker never boots → getDocument() hangs forever with no
//     exception.
//   - Committed copy in public/: a version snapshot with no link to
//     package.json (pdf.js hard-errors on worker/library mismatch), and
//     public/ is clobbered by the docker-compose watch sync, so nothing may
//     be generated there.
// Resolving from node_modules keeps the worker version locked to the installed
// pdfjs-dist automatically — no pinned URL, no committed binary.

const WORKER_URL = new URL(
  "pdfjs-dist/legacy/build/pdf.worker.min.js",
  import.meta.url,
).toString();

// ─── Minimal structural types for the pdf.js 3.11 surface we use ────────────
// One page type serves both consumers: text extraction (SMRTCheques) and
// canvas rendering (the SMRT Docs placement modal and signing view).

export interface PdfTextItem {
  str: string;
  transform?: number[];
}

export interface PdfViewport {
  width: number;
  height: number;
}

export interface PdfPage {
  getTextContent(): Promise<{ items: PdfTextItem[] }>;
  getViewport(opts: { scale: number }): PdfViewport;
  render(opts: {
    canvasContext: CanvasRenderingContext2D;
    viewport: PdfViewport;
  }): { promise: Promise<void> };
}

export interface PdfDocument {
  numPages: number;
  getPage(n: number): Promise<PdfPage>;
}

// GlobalWorkerOptions is deliberately absent from this type so call sites
// cannot reassign the worker.
export interface PdfJs {
  getDocument(src: { data: ArrayBuffer | Uint8Array }): {
    promise: Promise<PdfDocument>;
  };
}

let pdfjsPromise: Promise<PdfJs> | null = null;

/**
 * Lazily import pdf.js (code-split "pdfjs" chunk, fetched on first use), set
 * workerSrc exactly once, and dedupe concurrent callers via the memoized
 * promise.
 */
export function loadPdfjs(): Promise<PdfJs> {
  if (!pdfjsPromise) {
    const p = import(
      /* webpackChunkName: "pdfjs" */ "pdfjs-dist/legacy/build/pdf"
    ).then((mod) => {
      const pdfjs = mod as unknown as PdfJs & {
        GlobalWorkerOptions: { workerSrc: string };
      };
      pdfjs.GlobalWorkerOptions.workerSrc = WORKER_URL;
      return pdfjs as PdfJs;
    });
    // Don't cache a transient chunk-fetch failure forever — clear the memo so
    // the next call can retry.
    p.catch(() => {
      if (pdfjsPromise === p) pdfjsPromise = null;
    });
    pdfjsPromise = p;
  }
  return pdfjsPromise;
}
